// Next-step card of the deal cockpit (NBA shock package, batch 2).
//
// The card never guesses: it renders the engine's ranked action, its "why"
// codes localized through the catalog, and — for server actions — a preview
// with the promised before → after score. The server re-derives the action on
// execute, so the preview is a forecast, not a command.
//
// Navigate actions (`fill_qualification`, `advance_stage`) never call the API:
// the only honest write path for them is the card itself, passed in as
// `onNavigate`.
import { useState, type ReactElement } from 'react';
import { Alert, App, Button, Modal, Skeleton } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';

import { EntityMetric } from '../../../components/entity-detail/EntityMetric.js';
import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import {
  NBA_CONFIDENCE_LABEL_KEY,
  NBA_IMPACT_LABEL_KEY,
  NBA_LABEL_KEY_PREFIX,
  nbaActionTitleText,
  nbaImpactText,
  nbaReasonText,
  type NbaLabelI18n,
} from '../../../shared/format/nba-labels.js';
import type { NbaActionType } from '../../../sales-health/nba-engine.js';
import type { DealNextStepAction, ExecuteDealNextStepInput } from '../api/deals.client.js';
import {
  isStaleNextStepError,
  useDealNextStep,
  useExecuteDealNextStep,
} from '../hooks/useDealNextStep.js';
import { scoreToneColor } from '../lib/score-tone.js';

export interface DealNextStepCardProps {
  dealId: string;
  /** Navigate actions point at the deal UI; they never mutate through the API. */
  onNavigate: (action: DealNextStepAction) => void;
}

/** Localized task text; the server stores exactly what it receives. */
function executeInputOf(action: DealNextStepAction, i18n: NbaLabelI18n): ExecuteDealNextStepInput {
  const base: ExecuteDealNextStepInput = { actionId: action.actionId, type: action.type };
  if (action.type === 'schedule_activity') {
    return { ...base, title: i18n.t(`${NBA_LABEL_KEY_PREFIX}task.schedule`) };
  }
  if (action.type === 'log_touch') {
    return { ...base, title: i18n.t(`${NBA_LABEL_KEY_PREFIX}touch.subject`) };
  }
  return base;
}

/** Card-side destination of a navigate action. */
function navigateLabelKey(type: NbaActionType): string {
  return type === 'advance_stage'
    ? `${NBA_LABEL_KEY_PREFIX}navigate.stage`
    : `${NBA_LABEL_KEY_PREFIX}navigate.qualification`;
}

export function DealNextStepCard({ dealId, onNavigate }: DealNextStepCardProps): ReactElement {
  const { t, tc } = useTranslation();
  const { message } = App.useApp();
  const labels: NbaLabelI18n = { t, tc };
  const nextStep = useDealNextStep(dealId);
  const execute = useExecuteDealNextStep(dealId);
  const [previewOpen, setPreviewOpen] = useState(false);
  const action = nextStep.data?.action ?? null;

  const runExecute = (target: DealNextStepAction): void => {
    execute.mutate(executeInputOf(target, labels), {
      onSuccess: () => setPreviewOpen(false),
      onError: (error) => {
        // Stale is not a failure of the user's action: the hook refreshes the
        // card, and the modal closes onto the new recommendation.
        setPreviewOpen(false);
        if (isStaleNextStepError(error)) return;
        void message.error(t(`${NBA_LABEL_KEY_PREFIX}error.execute`));
      },
    });
  };

  return (
    <section className="entity-panel deal-next-step" data-testid="deal-next-step-card">
      <h2>{t(`${NBA_LABEL_KEY_PREFIX}card.title`)}</h2>

      {execute.data ? (
        <div className="deal-next-step-result" data-testid="deal-next-step-result">
          <Alert
            type="success"
            showIcon
            title={t(`${NBA_LABEL_KEY_PREFIX}result.done`, {
              before: execute.data.beforeScore,
              after: execute.data.afterScore,
            })}
          />
        </div>
      ) : null}

      {nextStep.isPending ? (
        <div data-testid="deal-next-step-loading">
          <Skeleton active title={false} paragraph={{ rows: 2 }} />
        </div>
      ) : nextStep.isError ? (
        <div data-testid="deal-next-step-error">
          <Alert
            type="error"
            showIcon
            title={t(`${NBA_LABEL_KEY_PREFIX}error.load`)}
            action={
              <Button size="small" onClick={() => void nextStep.refetch()}>
                {t('crm.shared.retry')}
              </Button>
            }
          />
        </div>
      ) : action === null ? (
        <div className="deal-next-step-empty" data-testid="deal-next-step-empty">
          <p className="deal-next-step-empty-title">{t(`${NBA_LABEL_KEY_PREFIX}empty`)}</p>
          <p className="entity-meta">{t(`${NBA_LABEL_KEY_PREFIX}empty.hint`)}</p>
        </div>
      ) : (
        <div className="deal-next-step-body">
          <div className="deal-next-step-head">
            <h3 className="deal-next-step-title" data-testid="deal-next-step-title">
              {nbaActionTitleText(labels, action.type)}
            </h3>
            <span className="deal-next-step-confidence">
              <span className="entity-meta">{t(NBA_CONFIDENCE_LABEL_KEY)}</span>
              <span
                className="deal-next-step-confidence-value"
                data-testid="deal-next-step-confidence"
              >
                {action.confidence}%
              </span>
            </span>
          </div>

          <ul className="deal-next-step-reasons" data-testid="deal-next-step-reasons">
            {action.reasonCodes.map((code) => (
              <li key={code}>{nbaReasonText(labels, code, action.reasonParams)}</li>
            ))}
          </ul>

          <p className="deal-next-step-effect entity-meta">
            {t(`${NBA_LABEL_KEY_PREFIX}preview.effect.${action.type}`)}
          </p>

          {action.meta.overdueTasks > 0 || action.meta.targetStage !== null ? (
            <div className="deal-next-step-meta entity-meta">
              {action.meta.overdueTasks > 0 ? (
                <span>
                  {t(`${NBA_LABEL_KEY_PREFIX}meta.overdueTasks`, {
                    count: action.meta.overdueTasks,
                  })}
                </span>
              ) : null}
              {action.meta.targetStage !== null ? (
                <span>
                  {t(`${NBA_LABEL_KEY_PREFIX}meta.targetStage`, {
                    stage: getEnumLabel('dealStage', action.meta.targetStage, t),
                  })}
                </span>
              ) : null}
            </div>
          ) : null}

          <div className="deal-next-step-metrics">
            <EntityMetric
              testId="deal-next-step-score-current"
              label={t(`${NBA_LABEL_KEY_PREFIX}score.current`)}
              value={`${action.currentScore}%`}
            />
            <EntityMetric
              testId="deal-next-step-score-projected"
              label={t(`${NBA_LABEL_KEY_PREFIX}score.projected`)}
              value={
                <span style={{ color: scoreToneColor(action.projectedScore) }}>
                  {action.projectedScore}%
                </span>
              }
            />
            <EntityMetric
              testId="deal-next-step-impact"
              label={t(NBA_IMPACT_LABEL_KEY)}
              value={nbaImpactText(labels, action.projectedImpact)}
            />
          </div>

          <div className="deal-next-step-actions">
            {action.executeMode === 'server' ? (
              <Button
                type="primary"
                data-testid="deal-next-step-execute"
                loading={execute.isPending}
                onClick={() => setPreviewOpen(true)}
              >
                {t(`${NBA_LABEL_KEY_PREFIX}execute`)}
              </Button>
            ) : (
              <Button
                type="primary"
                data-testid="deal-next-step-navigate"
                onClick={() => onNavigate(action)}
              >
                {t(navigateLabelKey(action.type))}
              </Button>
            )}
          </div>
        </div>
      )}

      <Modal
        open={previewOpen && action !== null}
        title={t(`${NBA_LABEL_KEY_PREFIX}preview.title`)}
        okText={t(`${NBA_LABEL_KEY_PREFIX}execute`)}
        cancelText={t('crm.entity_detail.cancel')}
        confirmLoading={execute.isPending}
        onOk={() => {
          if (action !== null) runExecute(action);
        }}
        onCancel={() => setPreviewOpen(false)}
        closable={{ 'aria-label': t('crm.entity_detail.cancel') }}
        destroyOnHidden
      >
        {action !== null ? (
          <div className="deal-next-step-preview" data-testid="deal-next-step-preview">
            <p className="deal-next-step-preview-title">
              {nbaActionTitleText(labels, action.type)}
            </p>
            <p>{t(`${NBA_LABEL_KEY_PREFIX}preview.effect.${action.type}`)}</p>
            <p className="deal-next-step-preview-score">
              <span>
                {t(`${NBA_LABEL_KEY_PREFIX}score.current`)}: {action.currentScore}%
              </span>
              <span aria-hidden="true">→</span>
              <span style={{ color: scoreToneColor(action.projectedScore) }}>
                {t(`${NBA_LABEL_KEY_PREFIX}score.projected`)}: {action.projectedScore}%
              </span>
            </p>
            <p className="entity-meta">{nbaImpactText(labels, action.projectedImpact)}</p>
          </div>
        ) : null}
      </Modal>
    </section>
  );
}
